<script setup>
/**
 * Pipeline Summary Metrics - Métricas principais do Pipeline
 * Design moderno com cards de métricas e indicadores de tendência
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import MetricHelp from '../MetricHelp.vue';

const props = defineProps({
  summary: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// Cores para os cards
const colors = {
  blue: '#3b82f6',
  green: '#10b981',
  purple: '#8b5cf6',
  orange: '#f59e0b',
  cyan: '#06b6d4',
  pink: '#ec4899',
};

// Helpers - defined before computed properties
const formatNumber = num => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString(browserLocale.value);
};

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Computed: Métricas principais
const metrics = computed(() => {
  if (!props.summary) return [];

  return [
    {
      key: 'total_cards',
      name: t('KANBAN_REPORTS.METRICS.TOTAL_CARDS.NAME'),
      value: props.summary.total_cards || 0,
      formattedValue: formatNumber(props.summary.total_cards || 0),
      color: colors.blue,
      icon: 'i-ph-stack',
    },
    {
      key: 'total_value',
      name: t('KANBAN_REPORTS.METRICS.TOTAL_VALUE.NAME'),
      value: props.summary.total_value || 0,
      formattedValue: formatCurrency(props.summary.total_value || 0),
      color: colors.green,
      icon: 'i-ph-currency-dollar',
    },
    {
      key: 'average_time',
      name: t('KANBAN_REPORTS.METRICS.AVERAGE_TIME.NAME'),
      value: props.summary.average_time_in_stage?.seconds || 0,
      formattedValue: props.summary.average_time_in_stage?.formatted || '0s',
      color: colors.purple,
      icon: 'i-ph-clock',
    },
    {
      key: 'total_funnels',
      name: t('KANBAN_REPORTS.METRICS.TOTAL_FUNNELS.NAME'),
      value: props.summary.total_funnels || 0,
      formattedValue: formatNumber(props.summary.total_funnels || 0),
      color: colors.orange,
      icon: 'i-ph-funnel',
    },
  ];
});

// Computed: Status cards — vêm do dicionário CRM_METRICS (mesmo nome e "?" em todas as telas)
const statusMetrics = computed(() => {
  if (!props.summary?.cards_by_status) return [];

  return [
    {
      key: 'open',
      // Aberto aqui é dos cards criados no período; não é o "Em aberto" do dicionário.
      metricKey: null,
      name: t('KANBAN_REPORTS.METRICS.OPEN_CARDS'),
      value: props.summary.cards_by_status.open || 0,
      color: colors.blue,
    },
    {
      key: 'won',
      metricKey: 'SALES',
      name: t('CRM_METRICS.SALES.NAME'),
      value: props.summary.cards_by_status.won || 0,
      color: colors.green,
    },
    {
      key: 'lost',
      metricKey: 'LOSSES',
      name: t('CRM_METRICS.LOSSES.NAME'),
      value: props.summary.cards_by_status.lost || 0,
      color: colors.orange,
    },
  ];
});

// Computed: Taxa de ganho (vendas ÷ (vendas + perdas))
const winRate = computed(() => {
  return props.summary?.conversion_rate?.rate || 0;
});
</script>

<template>
  <div class="space-y-6">
    <!-- Métricas Principais -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="p-5 rounded-xl bg-n-solid-2 border border-n-weak"
      >
        <div class="flex items-center gap-3 mb-3">
          <div
            class="p-2 rounded-lg"
            :style="{ backgroundColor: `${metric.color}15` }"
          >
            <span
              :class="metric.icon"
              class="text-lg"
              :style="{ color: metric.color }"
            />
          </div>
          <span class="text-sm text-n-slate-11">{{ metric.name }}</span>
        </div>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ metric.formattedValue }}
        </span>
      </div>
    </div>

    <!-- Status e Conversão -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <!-- Status Cards -->
      <div
        v-for="status in statusMetrics"
        :key="status.key"
        class="p-5 rounded-xl bg-n-solid-2 border border-n-weak"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="flex items-center gap-1.5 text-sm text-n-slate-11">
            {{ status.name }}
            <MetricHelp
              v-if="status.metricKey"
              :metric-key="status.metricKey"
            />
          </span>
          <div
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: status.color }"
          />
        </div>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ status.value }}
        </span>
      </div>

      <!-- Taxa de Conversão -->
      <div
        class="p-5 rounded-xl bg-n-solid-2 border border-n-weak flex flex-col items-center justify-center"
      >
        <div class="flex items-center gap-2 mb-3">
          <div
            class="p-2 rounded-lg"
            :style="{ backgroundColor: `${colors.pink}15` }"
          >
            <span
              class="i-ph-chart-line-up text-lg"
              :style="{ color: colors.pink }"
            />
          </div>
          <span class="flex items-center gap-1.5 text-sm text-n-slate-11">
            {{ $t('CRM_METRICS.WIN_RATE.NAME') }}
            <MetricHelp metric-key="WIN_RATE" />
          </span>
        </div>
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ winRate }}%
        </span>
      </div>
    </div>

    <!-- Top Pipeline -->
    <div
      v-if="summary?.top_performing_funnel"
      class="p-5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white"
    >
      <div
        class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
      >
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-lg">
            <span class="i-ph-trophy text-2xl" />
          </div>
          <div>
            <p class="text-sm font-medium opacity-90">
              {{ $t('KANBAN_REPORTS.TOP_PERFORMING_FUNNEL') }}
            </p>
            <h3 class="text-xl font-semibold">
              {{ summary.top_performing_funnel.name }}
            </h3>
          </div>
        </div>
        <div class="flex items-center gap-8">
          <div class="text-center">
            <p class="text-sm opacity-90">
              {{ $t('KANBAN_REPORTS.CHARTS.CARDS') }}
            </p>
            <p class="text-2xl font-semibold">
              {{ summary.top_performing_funnel.card_count }}
            </p>
          </div>
          <div class="h-12 w-px bg-white/30" />
          <div class="text-center">
            <p class="text-sm opacity-90">
              {{ $t('KANBAN_REPORTS.TOTAL_VALUE') }}
            </p>
            <p class="text-2xl font-semibold">
              {{ formatCurrency(summary.top_performing_funnel.total_value) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
